import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, Plus, Upload, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import ParameterEditor from '../components/ParameterEditor';

/* ─── helpers ────────────────────────────────────────────── */
const emptyParam = () => ({ name: '', weight: 0, description: '' });
const totalWeight = (params) => params.reduce((s, p) => s + Number(p.weight || 0), 0);

const SOPManagement = () => {
  const [sops, setSops] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ── create dialog ─────────────────────────────────────── */
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [checklistForm, setChecklistForm] = useState({
    title: '', description: '', items: [''],
    parameters: [emptyParam()],
  });
  const [documentForm, setDocumentForm] = useState({
    title: '', description: '', file: null,
    parameters: [emptyParam()],
  });

  /* ── edit dialog ───────────────────────────────────────── */
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingSOP, setEditingSOP] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '', description: '',
    items: [''],           // only for checklist
    parameters: [emptyParam()],
  });

  /* ── delete confirm ────────────────────────────────────── */
  const [deleteTarget, setDeleteTarget] = useState(null);

  /* ── fetch ─────────────────────────────────────────────── */
  useEffect(() => { fetchSOPs(); }, []);

  const fetchSOPs = async () => {
    try {
      const res = await axios.get(`${API}/sops`);
      setSops(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load SOPs');
    } finally {
      setLoading(false);
    }
  };

  /* ── CREATE – checklist ─────────────────────────────────── */
  const handleChecklistSubmit = async (e) => {
    e.preventDefault();
    if (totalWeight(checklistForm.parameters) !== 100) {
      toast.error(`Weights must total 100%. Current: ${totalWeight(checklistForm.parameters)}%`);
      return;
    }
    try {
      await axios.post(`${API}/sops`, {
        title: checklistForm.title,
        description: checklistForm.description,
        type: 'checklist',
        content: { items: checklistForm.items.filter((i) => i.trim()) },
        parameters: checklistForm.parameters.filter((p) => p.name.trim()),
      });
      toast.success('Checklist SOP created!');
      setIsCreateOpen(false);
      setChecklistForm({ title: '', description: '', items: [''], parameters: [emptyParam()] });
      fetchSOPs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create SOP');
    }
  };

  /* ── CREATE – document ──────────────────────────────────── */
  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    if (totalWeight(documentForm.parameters) !== 100) {
      toast.error(`Weights must total 100%. Current: ${totalWeight(documentForm.parameters)}%`);
      return;
    }
    try {
      const fd = new FormData();
      fd.append('title', documentForm.title);
      fd.append('description', documentForm.description || '');
      fd.append('file', documentForm.file);
      fd.append('parameters', JSON.stringify(documentForm.parameters.filter((p) => p.name.trim())));
      await axios.post(`${API}/sops/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document SOP uploaded!');
      setIsCreateOpen(false);
      setDocumentForm({ title: '', description: '', file: null, parameters: [emptyParam()] });
      fetchSOPs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload SOP');
    }
  };

  /* ── EDIT – open ────────────────────────────────────────── */
  const openEdit = (sop) => {
    setEditingSOP(sop);
    setEditForm({
      title: sop.title,
      description: sop.description || '',
      items: sop.content?.items?.length ? sop.content.items : [''],
      parameters: sop.parameters?.length ? sop.parameters.map((p) => ({ ...p })) : [emptyParam()],
    });
    setIsEditOpen(true);
  };

  /* ── EDIT – submit ──────────────────────────────────────── */
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (totalWeight(editForm.parameters) !== 100) {
      toast.error(`Weights must total 100%. Current: ${totalWeight(editForm.parameters)}%`);
      return;
    }
    try {
      const payload = {
        title: editForm.title,
        description: editForm.description,
        parameters: editForm.parameters.filter((p) => p.name.trim()),
      };
      // Only update content for checklist
      if (editingSOP.type === 'checklist') {
        payload.content = { items: editForm.items.filter((i) => i.trim()) };
      }
      await axios.put(`${API}/sops/${editingSOP.id}`, payload);
      toast.success('SOP updated successfully!');
      setIsEditOpen(false);
      setEditingSOP(null);
      fetchSOPs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update SOP');
    }
  };

  /* ── DELETE ─────────────────────────────────────────────── */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/sops/${deleteTarget.id}`);
      toast.success('SOP deleted successfully!');
      setDeleteTarget(null);
      fetchSOPs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete SOP');
    }
  };

  /* ── param / item helpers ───────────────────────────────── */
  const updateParam = (setter, form, idx, field, val) => {
    const params = [...form.parameters];
    params[idx] = { ...params[idx], [field]: val };
    setter({ ...form, parameters: params });
  };
  const addParam = (setter, form) =>
    setter({ ...form, parameters: [...form.parameters, emptyParam()] });
  const removeParam = (setter, form, idx) =>
    setter({ ...form, parameters: form.parameters.filter((_, i) => i !== idx) });

  const updateItem = (items, idx, val, setter, form) => {
    const next = [...items]; next[idx] = val;
    setter({ ...form, items: next });
  };

  /* ─────────────────────────────────────────────────────────
     RENDER
  ────────────────────────────────────────────────────────── */
  return (
    <Layout>
      <div className="space-y-8 fade-in" data-testid="sop-management-page">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              SOP Management
            </h1>
            <p className="text-slate-600">Create and manage Standard Operating Procedures</p>
          </div>

          {/* CREATE dialog */}
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#4338CA] hover:bg-[#3730A3] text-white" data-testid="create-sop-button">
                <Plus size={20} className="mr-2" />Create SOP
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Create New SOP</DialogTitle></DialogHeader>
              <Tabs defaultValue="checklist" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="checklist" data-testid="checklist-tab">Checklist</TabsTrigger>
                  <TabsTrigger value="document" data-testid="document-tab">Upload Document</TabsTrigger>
                </TabsList>

                {/* ── Checklist tab ── */}
                <TabsContent value="checklist">
                  <form onSubmit={handleChecklistSubmit} className="space-y-4" data-testid="checklist-form">
                    <div>
                      <Label>Title</Label>
                      <Input placeholder="e.g., Customer Support Call Protocol"
                        value={checklistForm.title}
                        onChange={(e) => setChecklistForm({ ...checklistForm, title: e.target.value })}
                        required data-testid="checklist-title-input" />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea placeholder="Brief description" value={checklistForm.description}
                        onChange={(e) => setChecklistForm({ ...checklistForm, description: e.target.value })}
                        data-testid="checklist-description-input" />
                    </div>
                    <div>
                      <Label>Checklist Items</Label>
                      <div className="space-y-2 mt-2">
                        {checklistForm.items.map((item, idx) => (
                          <div key={idx} className="flex gap-2">
                            <Input placeholder={`Item ${idx + 1}`} value={item}
                              onChange={(e) => updateItem(checklistForm.items, idx, e.target.value, setChecklistForm, checklistForm)}
                              data-testid={`checklist-item-${idx}`} />
                            {checklistForm.items.length > 1 && (
                              <Button type="button" variant="outline" size="sm"
                                onClick={() => setChecklistForm({ ...checklistForm, items: checklistForm.items.filter((_, i) => i !== idx) })}>
                                Remove
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                      <Button type="button" variant="outline" size="sm" className="mt-2"
                        onClick={() => setChecklistForm({ ...checklistForm, items: [...checklistForm.items, ''] })}
                        data-testid="add-checklist-item-button">
                        <Plus size={14} className="mr-1" />Add Item
                      </Button>
                    </div>
                    <ParameterEditor
                      params={checklistForm.parameters}
                      onAdd={() => addParam(setChecklistForm, checklistForm)}
                      onUpdate={(idx, f, v) => updateParam(setChecklistForm, checklistForm, idx, f, v)}
                      onRemove={(idx) => removeParam(setChecklistForm, checklistForm, idx)}
                    />
                    <Button type="submit" className="w-full" data-testid="submit-checklist-button">
                      Create Checklist SOP
                    </Button>
                  </form>
                </TabsContent>

                {/* ── Document tab ── */}
                <TabsContent value="document">
                  <form onSubmit={handleDocumentUpload} className="space-y-4" data-testid="document-form">
                    <div>
                      <Label>Title</Label>
                      <Input placeholder="e.g., Sales Call Guidelines" value={documentForm.title}
                        onChange={(e) => setDocumentForm({ ...documentForm, title: e.target.value })}
                        required data-testid="document-title-input" />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea placeholder="Brief description" value={documentForm.description}
                        onChange={(e) => setDocumentForm({ ...documentForm, description: e.target.value })}
                        data-testid="document-description-input" />
                    </div>
                    <div>
                      <Label>Upload PDF Document</Label>
                      <Input type="file" accept=".pdf" className="mt-1"
                        onChange={(e) => setDocumentForm({ ...documentForm, file: e.target.files[0] })}
                        required data-testid="document-file-input" />
                    </div>
                    <ParameterEditor
                      params={documentForm.parameters}
                      onAdd={() => addParam(setDocumentForm, documentForm)}
                      onUpdate={(idx, f, v) => updateParam(setDocumentForm, documentForm, idx, f, v)}
                      onRemove={(idx) => removeParam(setDocumentForm, documentForm, idx)}
                    />
                    <Button type="submit" className="w-full" data-testid="submit-document-button">
                      Upload SOP Document
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
        </div>

        {/* ── SOPs Grid ──────────────────────────────────────── */}
        {loading ? (
          <p className="text-slate-600">Loading SOPs...</p>
        ) : sops.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sops.map((sop) => (
              <Card key={sop.id} className="p-6 border border-slate-200 shadow-sm card-hover"
                data-testid={`sop-card-${sop.id}`}>
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-indigo-50 rounded-lg shrink-0">
                    <FileText size={24} className="text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-bold text-slate-900 mb-1 truncate">{sop.title}</h3>
                      {/* Edit + Delete buttons */}
                      <div className="flex gap-1 shrink-0">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-500 hover:text-indigo-600"
                          onClick={() => openEdit(sop)} data-testid={`edit-sop-${sop.id}`}
                          title="Edit SOP">
                          <Pencil size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-500 hover:text-rose-600"
                          onClick={() => setDeleteTarget(sop)} data-testid={`delete-sop-${sop.id}`}
                          title="Delete SOP">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 mb-3">{sop.description || 'No description'}</p>
                    {sop.parameters?.length > 0 && (
                      <div className="mb-3 space-y-1">
                        <p className="text-xs font-semibold text-slate-700">Parameters:</p>
                        {sop.parameters.map((param, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs text-slate-600">
                            <span className="font-medium">{param.name}</span>
                            <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded">{param.weight}%</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200"
                        data-testid={`sop-type-${sop.id}`}>{sop.type}</span>
                      <span className="text-xs text-slate-500">{new Date(sop.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center border border-slate-200 shadow-sm">
            <FileText size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 mb-4">No SOPs created yet</p>
            <Button onClick={() => setIsCreateOpen(true)} data-testid="create-first-sop-button">
              Create First SOP
            </Button>
          </Card>
        )}

        {/* ── EDIT dialog ────────────────────────────────────── */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit SOP</DialogTitle>
            </DialogHeader>
            {editingSOP && (
              <form onSubmit={handleEditSubmit} className="space-y-4" data-testid="edit-sop-form">
                <div>
                  <Label>Title</Label>
                  <Input value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    required data-testid="edit-title-input" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    data-testid="edit-description-input" />
                </div>

                {/* Checklist items only for checklist type */}
                {editingSOP.type === 'checklist' && (
                  <div>
                    <Label>Checklist Items</Label>
                    <div className="space-y-2 mt-2">
                      {editForm.items.map((item, idx) => (
                        <div key={idx} className="flex gap-2">
                          <Input placeholder={`Item ${idx + 1}`} value={item}
                            onChange={(e) => {
                              const next = [...editForm.items]; next[idx] = e.target.value;
                              setEditForm({ ...editForm, items: next });
                            }}
                            data-testid={`edit-item-${idx}`} />
                          {editForm.items.length > 1 && (
                            <Button type="button" variant="outline" size="sm"
                              onClick={() => setEditForm({ ...editForm, items: editForm.items.filter((_, i) => i !== idx) })}>
                              Remove
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                    <Button type="button" variant="outline" size="sm" className="mt-2"
                      onClick={() => setEditForm({ ...editForm, items: [...editForm.items, ''] })}>
                      <Plus size={14} className="mr-1" />Add Item
                    </Button>
                  </div>
                )}

                {/* Document type — note about re-upload */}
                {editingSOP.type === 'document' && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                    <Upload size={14} className="inline mr-1" />
                    Document content is read-only. To change the PDF, delete this SOP and re-upload.
                  </div>
                )}

                <ParameterEditor
                  params={editForm.parameters}
                  onAdd={() => setEditForm({ ...editForm, parameters: [...editForm.parameters, emptyParam()] })}
                  onUpdate={(idx, field, val) => {
                    const params = [...editForm.parameters];
                    params[idx] = { ...params[idx], [field]: val };
                    setEditForm({ ...editForm, parameters: params });
                  }}
                  onRemove={(idx) => setEditForm({ ...editForm, parameters: editForm.parameters.filter((_, i) => i !== idx) })}
                />

                <div className="flex gap-3 pt-2">
                  <Button type="submit" className="flex-1 bg-[#4338CA] hover:bg-[#3730A3] text-white"
                    data-testid="save-sop-button">
                    Save Changes
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* ── DELETE confirm ──────────────────────────────────── */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete SOP?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to permanently delete <strong>{deleteTarget?.title}</strong>?
                This action cannot be undone and any existing assessments referencing this SOP will remain but the SOP data will be unavailable.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-rose-600 hover:bg-rose-700 text-white"
                onClick={handleDelete}
                data-testid="confirm-delete-sop">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

      </div>
    </Layout>
  );
};

export default SOPManagement;
